import React from "react";
import PropTypes from "prop-types";
import { H4 } from "components/core/Typography";
import { DropdownItem } from "components/molecules";
import { SectionHeading } from "components/atoms";
import { GTM_CLASSNAMES } from "config/gtmSelectors";
import ChevronRightSvg from "assets/images/slp/chevron-right.svg";
import AmenitiesIconSvg from "assets/images/slp/amenities-icon.svg";
import SizeguideIconSvg from "assets/icons/size-guide.inline.svg";
import VehicleIconSvg from "assets/icons/parking-space.inline.svg";
import { storageMap } from "./storageMap";
import "./CitySection.scss";

function DrilldownLinks({ items, name }) {
	return (
		<div className="slp-accordion-links">
			{items.map((item) => {
				const matched = storageMap.find((t) =>
					item?.text?.toLowerCase().includes(t.key.toLowerCase())
				);
				const result = matched
					? `${matched.label} in ${name}`
					: item?.text;
				return (
					<a
						key={item.uri}
						href={`/${item.uri}`}
						className="slp-accordion-links__item"
					>
						<H4
							className="slp-accordion-links__text"
							size="small"
						>
							{result}
						</H4>
						<img
							src={ChevronRightSvg}
							className="slp-accordion-links__chevron"
							alt=""
							aria-hidden="true"
							loading="lazy"
							width="16"
							height="16"
						/>
					</a>
				);
			})}
		</div>
	);
}

DrilldownLinks.propTypes = {
	name: PropTypes.string.isRequired,
	items: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			uri: PropTypes.string.isRequired
		})
	).isRequired
};

function CityDropdown({ icon, label, items, name, buttonClassName }) {
	return (
		<div className="slp-city-section__dropdowns">
			<DropdownItem
				icon={icon}
				label={label}
				buttonClassName={buttonClassName}
			>
				{items?.length > 0 && (
					<DrilldownLinks
						items={items}
						name={name}
					/>
				)}
			</DropdownItem>
		</div>
	);
}

CityDropdown.propTypes = {
	icon: PropTypes.node.isRequired,
	label: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	buttonClassName: PropTypes.string,
	items: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string,
			uri: PropTypes.string
		})
	)
};

export default function CitySection({ city }) {
	const { name, url, links } = city;

	return (
		<div className="slp-city-section">
			<SectionHeading
				level="2"
				withUnderline
			>
				{name} Storage Units
			</SectionHeading>

			<CityDropdown
				icon={
					<img
						src={AmenitiesIconSvg}
						alt=""
						aria-hidden="true"
						loading="lazy"
						width="20"
						height="20"
					/>
				}
				label="Storage Unit Amenities"
				items={links?.amenities}
				name={name}
				buttonClassName={GTM_CLASSNAMES.DROPDOWN_ITEM_AMENITIES}
			/>

			<CityDropdown
				icon={<SizeguideIconSvg aria-hidden="true" />}
				label="Storage Unit Sizes"
				items={links?.unitTypes}
				name={name}
				buttonClassName={GTM_CLASSNAMES.DROPDOWN_ITEM_UNIT_SIZES}
			/>

			<CityDropdown
				icon={
					<VehicleIconSvg
						className="slp-city-section__vehicle-icon"
						aria-hidden="true"
					/>
				}
				label="Vehicle Storage Units"
				items={links?.vehicleStorage}
				name={name}
				buttonClassName={GTM_CLASSNAMES.DROPDOWN_ITEM_VEHICLE_STORAGE}
			/>

			<a
				href={url}
				className="slp-city-section__cta"
			>
				View all storage units in {name}
			</a>
		</div>
	);
}

CitySection.propTypes = {
	city: PropTypes.shape({
		name: PropTypes.string.isRequired,

		url: PropTypes.string,
		links: PropTypes.shape({
			amenities: PropTypes.arrayOf(
				PropTypes.shape({
					text: PropTypes.string,
					uri: PropTypes.string
				})
			),
			unitTypes: PropTypes.arrayOf(
				PropTypes.shape({
					text: PropTypes.string,
					uri: PropTypes.string
				})
			),
			vehicleStorage: PropTypes.arrayOf(
				PropTypes.shape({
					text: PropTypes.string,
					uri: PropTypes.string
				})
			)
		})
	}).isRequired
};
